import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Select from '../../FormFields/Select';
import {
  STORY_ABOVE_OPTIONS,
  STORY_OPTIONS,
  TYPE_COLORS,
  getFeatureTypeCode,
  getGarageKindName,
  livingSqft,
  measureSqft,
  sectionAbbrev,
} from './tapeUtils';
import styles from './TapeFeaturePanel.styles';

const useStyles = makeStyles(styles);

const findByName = (list, name) => (list || []).find(x => x.name === name);

const TapeFeaturePanel = ({ features, selectedId, tapeConfig, onSelect, onPatch, onRemove }) => {
  const classes = useStyles();
  const selected = features.find(f => f.id === selectedId) ?? null;
  const featureTypes = tapeConfig?.featureTypes || [];
  const foundationTypes = tapeConfig?.foundationTypes || [];
  const garageKinds = tapeConfig?.garageKinds || [];

  const living = features.reduce((s, f) => {
    const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
    const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
    return s + livingSqft(f, typeCode, garageKindName);
  }, 0);
  const nonLiving = features.reduce((s, f) => {
    const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
    if (typeCode === 'DWL') {
      return s;
    }
    return s + measureSqft(f.vertices);
  }, 0);

  const selectedTypeCode = selected ? getFeatureTypeCode(selected.tapeFeatureTypeId, tapeConfig) : null;
  const selectedGarageKindName = selected ? getGarageKindName(selected.tapeGarageKindId, tapeConfig) : null;

  return (
    <div className={classes.panel}>
      <div className={classes.totalsCard}>
        <p className={classes.totalsLabel}>Living area</p>
        <p className={classes.totalsValue}>{Math.round(living).toLocaleString()} SF</p>
        <p className={classes.totalsSub}>
          {Math.round(nonLiving).toLocaleString()} SF non-living · {features.length} area{features.length === 1 ? '' : 's'}
        </p>
      </div>

      <div>
        <p className={classes.totalsLabel}>Areas</p>
        {features.map(f => {
          const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
          const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
          const foundationCode = foundationTypes.find(x => x.id === f.tapeFoundationTypeId)?.code || '';
          const liv = Math.round(livingSqft(f, typeCode, garageKindName));
          const fp = Math.round(measureSqft(f.vertices));
          return (
            <button
              type="button"
              key={f.id}
              onClick={() => onSelect(f.id)}
              className={f.id === selectedId ? `${classes.listItem} ${classes.listItemSelected}` : classes.listItem}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span className={classes.swatch} style={{ backgroundColor: TYPE_COLORS[typeCode] || TYPE_COLORS.OTH }} />
                <span className={classes.code}>{sectionAbbrev(f, typeCode, foundationCode, garageKindName)}</span>
              </span>
              <span className={classes.code}>{liv ? `${liv.toLocaleString()} GLA` : fp.toLocaleString()}</span>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className={classes.editCard}>
          <p className={classes.totalsLabel}>Selected</p>
          <div className={classes.select}>
            <Select
              label="Type"
              optionValueKey="id"
              optionLabelKey="name"
              options={featureTypes}
              value={selected.tapeFeatureTypeId}
              onChange={val => {
                const newType = featureTypes.find(x => x.id === val);
                const newTypeCode = newType?.code;
                if (newTypeCode === 'GAR') {
                  const standard = findByName(garageKinds, 'Standard');
                  onPatch(selected.id, { tapeFeatureTypeId: val, tapeGarageKindId: standard?.id ?? null, storiesAbove: 0 });
                } else if (newTypeCode === 'POR' || newTypeCode === 'BAL') {
                  onPatch(selected.id, { tapeFeatureTypeId: val, storiesAbove: 0 });
                } else {
                  onPatch(selected.id, { tapeFeatureTypeId: val });
                }
              }}
            />
          </div>

          {selectedTypeCode === 'DWL' && (
            <>
              <div className={classes.select}>
                <Select
                  label="Stories"
                  optionValueKey="value"
                  optionLabelKey="label"
                  options={[...STORY_OPTIONS.map(s => ({ value: s, label: String(s) })), { value: 'cathedral', label: 'Cathedral (1 st, two stories high)' }]}
                  value={selected.isCathedral ? 'cathedral' : String(selected.stories)}
                  onChange={val => {
                    if (val === 'cathedral') {
                      onPatch(selected.id, { stories: 1, isCathedral: true });
                      return;
                    }
                    onPatch(selected.id, { stories: Number(val), isCathedral: false });
                  }}
                />
              </div>
              {selected.isCathedral && (
                <p className={classes.helperText}>Two stories high, one story of living. GLA = footprint × 1. Stamp CATH.</p>
              )}
            </>
          )}

          {selectedTypeCode === 'GAR' && (
            <>
              <div className={classes.select}>
                <Select
                  label="Garage"
                  optionValueKey="id"
                  optionLabelKey="name"
                  options={garageKinds}
                  value={selected.tapeGarageKindId}
                  onChange={val => {
                    const kind = garageKinds.find(x => x.id === val);
                    const defaultAbove = kind?.name === 'Half' ? 0.5 : kind?.name === 'Built-in' || kind?.name === 'Basement' ? 1 : 0;
                    const basement = kind?.name === 'Basement' ? findByName(tapeConfig?.foundationTypes, 'Basement')?.id : selected.tapeFoundationTypeId;
                    onPatch(selected.id, { tapeGarageKindId: val, storiesAbove: defaultAbove, tapeFoundationTypeId: basement });
                  }}
                />
              </div>
              {(selectedGarageKindName === 'Built-in' || selectedGarageKindName === 'Basement') && (
                <>
                  <div className={classes.select}>
                    <Select
                      label="Living stories above"
                      optionValueKey="value"
                      optionLabelKey="label"
                      options={STORY_ABOVE_OPTIONS.filter(s => s !== 0).map(s => ({
                        value: s,
                        label: s === 0.5 ? '½ story' : `${s} ${s === 1 ? 'story' : 'stories'}`,
                      }))}
                      value={selected.storiesAbove ?? 0}
                      onChange={val => onPatch(selected.id, { storiesAbove: Number(val) })}
                    />
                  </div>
                </>
              )}
              {selectedGarageKindName === 'Half' && (
                <p className={classes.helperText}>Half story over garage adds 0.5 × garage footprint to GLA.</p>
              )}
            </>
          )}

          {(selectedTypeCode === 'POR' || selectedTypeCode === 'BAL') && (
            <>
              <div className={classes.select}>
                <Select
                  label="Living above"
                  optionValueKey="value"
                  optionLabelKey="label"
                  options={[
                    { value: 0, label: 'None — not in GLA' },
                    ...STORY_ABOVE_OPTIONS.filter(s => s !== 0).map(s => ({
                      value: s,
                      label: s === 0.5 ? '½ story' : `${s} ${s === 1 ? 'story' : 'stories'}`,
                    })),
                  ]}
                  value={selected.storiesAbove ?? 0}
                  onChange={val => onPatch(selected.id, { storiesAbove: Number(val) })}
                />
              </div>
              <p className={classes.helperText}>Porch and balcony stay out of GLA unless you set living above.</p>
            </>
          )}

          <div className={classes.select}>
            <Select
              label="Foundation"
              optionValueKey="id"
              optionLabelKey="name"
              options={foundationTypes}
              value={selected.tapeFoundationTypeId}
              onChange={val => onPatch(selected.id, { tapeFoundationTypeId: val })}
            />
          </div>

          <p className={classes.summary}>
            Footprint {Math.round(measureSqft(selected.vertices)).toLocaleString()} SF
            {livingSqft(selected, selectedTypeCode, selectedGarageKindName)
              ? ` · GLA ${Math.round(livingSqft(selected, selectedTypeCode, selectedGarageKindName)).toLocaleString()}`
              : ' · not in GLA'}
          </p>
          <button type="button" className={classes.removeLink} onClick={() => onRemove(selected.id)}>
            Remove area
          </button>
        </div>
      )}
    </div>
  );
};

TapeFeaturePanel.propTypes = {
  features: PropTypes.array,
  selectedId: PropTypes.string,
  tapeConfig: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onPatch: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default TapeFeaturePanel;
